import type { LiveStatusSummaryResource, KubernetesMachineLiveStatusResource, KubernetesLiveManifestDiff, ProjectGroupLiveStatusResource, ProjectLiveStatusResource, KubernetesEventResource, ContainerLogLineResource, MonitorErrorResource, KubernetesLiveStatusDetailedResource, KubernetesArgoCDInstanceLiveStatusResource, ResourceSourceType, } from "..";
import type { Client, RequestContext } from "../client";
export class KubernetesLiveStatusRepository {
    private client: Client;
    constructor(client: Client, private readonly requestContext: RequestContext) {
        this.client = client;
    }
    getLiveStatus(projectId: string, environmentId: string, tenantId?: string, summaryOnly: boolean = false): Promise<GetLiveStatusResponse> {
        const queryParams = summaryOnly ? "?summaryOnly=true" : "";
        if (tenantId === undefined) {
            return this.client.get<GetLiveStatusResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/livestatus${queryParams}`, {
                projectId: projectId,
                environmentId: environmentId,
            });
        }
        return this.client.get<GetLiveStatusResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/livestatus${queryParams}`, {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
        });
    }
    private getLiveStatusSourceName(sourceType: ResourceSourceType) {
        switch (sourceType) {
            case "ArgoCDApplication":
                return "argocdapplications";
            case "ArgoCDInstance":
                return "argocdinstances";
            case "KubernetesMonitor":
                return "machines";
            default:
                throw Error("Unknown ResourceSourceType");
        }
    }
    getResource(projectId: string, environmentId: string, tenantId: string | undefined, resourceSourceId: string, sourceType: ResourceSourceType, resourceId: string): Promise<GetResourceResponse> {
        const sourceName = this.getLiveStatusSourceName(sourceType);
        if (tenantId === undefined) {
            return this.client.get<GetResourceResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/${sourceName}/{sourceId}/resources/{desiredOrKubernetesMonitoredResourceId}`, {
                projectId: projectId,
                environmentId: environmentId,
                sourceId: resourceSourceId,
                desiredOrKubernetesMonitoredResourceId: resourceId,
            });
        }
        return this.client.get<GetResourceResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/${sourceName}/{sourceId}/resources/{desiredOrKubernetesMonitoredResourceId}`, {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
            sourceId: resourceSourceId,
            desiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getResourceManifest(projectId: string, environmentId: string, tenantId: string | undefined, resourceSourceId: string, sourceType: ResourceSourceType, resourceId: string): Promise<GetResourceManifestResponse> {
        const sourceName = this.getLiveStatusSourceName(sourceType);
        if (tenantId === undefined) {
            return this.client.get<GetResourceManifestResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/${sourceName}/{sourceId}/resources/{desiredOrKubernetesMonitoredResourceId}/manifest`, {
                projectId: projectId,
                environmentId: environmentId,
                sourceId: resourceSourceId,
                desiredOrKubernetesMonitoredResourceId: resourceId,
            });
        }
        return this.client.get<GetResourceManifestResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/${sourceName}/{sourceId}/resources/{desiredOrKubernetesMonitoredResourceId}/manifest`, {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
            sourceId: resourceSourceId,
            desiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getProjectDashboardLiveStatus(projectId: string): Promise<GetProjectDashboardLiveStatusBffResponse> {
        return this.client.get<GetProjectDashboardLiveStatusBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/livestatus", {
            projectId: projectId,
        });
    }
    getProjectsDashboardLiveStatus(projectOrGroupPartialName: string | undefined): Promise<GetProjectsDashboardLiveStatusBffResponse> {
        return this.client.get(this.requestContext, "~/bff/spaces/{spaceId}/projects/livestatus{?projectOrGroupPartialName}", { projectOrGroupPartialName });
    }
    beginContainerLogsSession(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string, podName: string, containerName: string, showPreviousContainer: boolean): Promise<BeginSessionResponse> {
        return this.client.post<BeginSessionResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/logs/sessions", {
            ProjectId: projectId,
            EnvironmentId: environmentId,
            TenantId: tenantId,
            MachineId: machineId,
            PodName: podName,
            ContainerName: containerName,
            ShowPreviousContainer: showPreviousContainer,
            DesiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getContainerLogs(sessionId: string): Promise<GetContainerLogsResponse> {
        return this.client.get<GetContainerLogsResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/logs/sessions/{sessionId}", {
            sessionId,
        });
    }
    getArgoCDContainerLogs(projectId: string, environmentId: string, tenantId: string | undefined, sourceId: string, resourceId: string, podName: string, containerName: string, showPreviousContainer: boolean): Promise<GetArgoCDContainerLogsResponse> {
        if (tenantId === undefined) {
            return this.client.get<GetContainerLogsResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/argocdapplications/{sourceId}/resources/{resourceId}/logs{?podName,containerName,showPreviousContainer}`, {
                projectId,
                environmentId,
                sourceId,
                resourceId: resourceId,
                podName,
                containerName,
                showPreviousContainer,
            });
        }
        return this.client.get<GetContainerLogsResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/argocdapplications/{sourceId}/resources/{resourceId}/logs{?podName,containerName,showPreviousContainer}`, {
            projectId,
            environmentId,
            tenantId,
            sourceId,
            resourceId: resourceId,
            podName,
            containerName,
            showPreviousContainer,
        });
    }
    beginResourceEventsSession(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string): Promise<BeginSessionResponse> {
        return this.client.post<BeginSessionResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/events/sessions", {
            ProjectId: projectId,
            EnvironmentId: environmentId,
            TenantId: tenantId,
            MachineId: machineId,
            DesiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getResourceEvents(sessionId: string): Promise<GetResourceEventsResponse> {
        return this.client.get<GetResourceEventsResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/events/sessions/{sessionId}", {
            sessionId,
        });
    }
    getArgoCDResourceEvents(projectId: string, environmentId: string, tenantId: string | undefined, resourceSourceId: string, sourceType: ResourceSourceType, resourceId: string): Promise<GetResourceEventsResponse> {
        if (sourceType === "KubernetesMonitor") {
            throw new Error("This endpoint only supports Argo CD live resources");
        }
        const sourceName = this.getLiveStatusSourceName(sourceType);
        if (tenantId === undefined) {
            return this.client.get<GetResourceEventsResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/${sourceName}/{sourceId}/resources/{desiredOrKubernetesMonitoredResourceId}/events`, {
                projectId: projectId,
                environmentId: environmentId,
                sourceId: resourceSourceId,
                desiredOrKubernetesMonitoredResourceId: resourceId,
            });
        }
        return this.client.get<GetResourceEventsResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/${sourceName}/{sourceId}/resources/{desiredOrKubernetesMonitoredResourceId}/events`, {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
            sourceId: resourceSourceId,
            desiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
}
export interface GetLiveStatusResponse {
    MachineStatuses: KubernetesMachineLiveStatusResource[];
    // TODO: #project-argo-cd-in-octopus make this required after we finalise the shape of the contract
    ArgoCDInstanceStatuses?: KubernetesArgoCDInstanceLiveStatusResource[];
    Summary: LiveStatusSummaryResource;
}
export interface GetResourceResponse {
    Resource: KubernetesLiveStatusDetailedResource;
}
export interface GetResourceManifestResponse {
    LiveManifest: string;
    DesiredManifest: string | null;
    Diff: KubernetesLiveManifestDiff | null;
}
export interface GetProjectsDashboardLiveStatusBffResponse {
    LiveStatusAvailableForSpace: boolean;
    LiveStatusSourceTypes: ResourceSourceType[];
    ProjectGroups: ProjectGroupLiveStatusResource[];
}
export interface GetProjectDashboardLiveStatusBffResponse {
    ProjectLiveStatus: ProjectLiveStatusResource;
}
export interface BeginSessionResponse {
    SessionId: string;
}
export interface GetContainerLogsResponse {
    Logs: ContainerLogLineResource[];
    IsSessionCompleted: boolean;
    Error: MonitorErrorResource | undefined;
}
export interface GetArgoCDContainerLogsResponse {
    Logs: ContainerLogLineResource[];
    Error: MonitorErrorResource | undefined;
}
export type GetResourceEventsResponse = {
    Events: KubernetesEventResource[];
    IsSessionCompleted: boolean;
    Error: MonitorErrorResource | undefined;
};
